import React, { useState } from 'react';
import { ShoppingCart, Plus, Search, Filter, Truck, Clock, CheckCircle, Package, Building, FileText, AlertCircle, Send } from 'lucide-react';
import Layout from './Layout';

/**
 * ProcurementPage - Parts ordering and supplier management
 */
const ProcurementPage = () => {
    const [activeTab, setActiveTab] = useState('orders');
    const [filter, setFilter] = useState('all');

    // Dummy purchase orders
    const purchaseOrders = [
        { id: 'PO-2024-001', supplier: 'Repco Trade', items: 5, total: 1250.00, status: 'delivered', orderDate: '2024-02-01', deliveryDate: '2024-02-05' },
        { id: 'PO-2024-002', supplier: 'Burson Auto', items: 3, total: 485.00, status: 'shipped', orderDate: '2024-02-07', deliveryDate: '2024-02-12' },
        { id: 'PO-2024-003', supplier: 'Auto One', items: 8, total: 2100.00, status: 'confirmed', orderDate: '2024-02-09', deliveryDate: '2024-02-14' },
        { id: 'PO-2024-004', supplier: 'Repco Trade', items: 2, total: 320.00, status: 'pending', orderDate: '2024-02-10', deliveryDate: null },
        { id: 'PO-2024-005', supplier: 'NGK Australia', items: 4, total: 580.00, status: 'draft', orderDate: '2024-02-10', deliveryDate: null },
    ];

    // Dummy suppliers
    const suppliers = [
        { id: 1, name: 'Repco Trade', contact: 'John Reynolds', email: 'trade@repco.com.au', phone: '1300 737 268', leadTime: '2-3 days', rating: 4.5, ordersYTD: 24, spendYTD: 18500 },
        { id: 2, name: 'Burson Auto', contact: 'Sarah Mitchell', email: 'orders@burson.com.au', phone: '1300 287 766', leadTime: '3-5 days', rating: 4.2, ordersYTD: 18, spendYTD: 12300 },
        { id: 3, name: 'Auto One', contact: 'Mike Thompson', email: 'wholesale@autoone.com.au', phone: '1300 286 663', leadTime: '1-2 days', rating: 4.8, ordersYTD: 32, spendYTD: 28700 },
        { id: 4, name: 'NGK Australia', contact: 'David Lee', email: 'sales@ngk.com.au', phone: '03 9793 5000', leadTime: '5-7 days', rating: 4.0, ordersYTD: 8, spendYTD: 4200 },
    ];

    // Suggested orders based on low stock
    const suggestedOrders = [
        { sku: 'FLT-OIL-TOY', name: 'Oil Filter - Toyota', current: 8, min: 15, suggested: 20, supplier: 'Repco Trade', unitPrice: 12.50 },
        { sku: 'CLN-BRK-500', name: 'Brake Cleaner 500ml', current: 2, min: 12, suggested: 24, supplier: 'Burson Auto', unitPrice: 8.50 },
        { sku: 'WIP-BLD-22', name: 'Wiper Blades 22"', current: 0, min: 6, suggested: 12, supplier: 'Auto One', unitPrice: 24.00 },
    ];

    const getStatusBadge = (status) => {
        const styles = {
            delivered: 'bg-emerald-100 text-emerald-700',
            shipped: 'bg-blue-100 text-blue-700',
            confirmed: 'bg-purple-100 text-purple-700',
            pending: 'bg-amber-100 text-amber-700',
            draft: 'bg-gray-100 text-gray-600',
        };
        const icons = {
            delivered: <CheckCircle size={12} />,
            shipped: <Truck size={12} />,
            confirmed: <FileText size={12} />,
            pending: <Clock size={12} />,
            draft: <FileText size={12} />,
        };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium capitalize ${styles[status]}`}>
                {icons[status]} {status}
            </span>
        );
    };

    const filteredOrders = purchaseOrders.filter(order => {
        if (filter !== 'all' && order.status !== filter) return false;
        return true;
    });

    return (
        <Layout>
            <div className="p-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-rose-50 rounded-xl flex items-center justify-center">
                            <ShoppingCart size={20} className="text-rose-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Procurement</h1>
                            <p className="text-sm text-gray-500">Order parts and manage suppliers</p>
                        </div>
                    </div>
                    <button className="flex items-center gap-2 px-4 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-colors font-medium">
                        <Plus size={18} />
                        New Purchase Order
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl mb-6 w-fit">
                    {[
                        { id: 'orders', label: 'Purchase Orders', icon: FileText },
                        { id: 'suppliers', label: 'Suppliers', icon: Building },
                        { id: 'suggested', label: 'Suggested Orders', icon: AlertCircle },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                                activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                            }`}
                        >
                            <tab.icon size={16} />
                            {tab.label}
                            {tab.id === 'suggested' && suggestedOrders.length > 0 && (
                                <span className="px-1.5 py-0.5 bg-rose-100 text-rose-700 rounded-full text-xs font-bold">
                                    {suggestedOrders.length}
                                </span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Purchase Orders Tab */}
                {activeTab === 'orders' && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                            <div className="bg-white p-4 rounded-xl border border-gray-200">
                                <p className="text-sm text-gray-500 mb-1">Open Orders</p>
                                <p className="text-2xl font-bold text-gray-900">{purchaseOrders.filter(o => o.status !== 'delivered').length}</p>
                            </div>
                            <div className="bg-white p-4 rounded-xl border border-gray-200">
                                <p className="text-sm text-gray-500 mb-1">Pending Approval</p>
                                <p className="text-2xl font-bold text-amber-600">{purchaseOrders.filter(o => o.status === 'pending').length}</p>
                            </div>
                            <div className="bg-white p-4 rounded-xl border border-gray-200">
                                <p className="text-sm text-gray-500 mb-1">In Transit</p>
                                <p className="text-2xl font-bold text-blue-600">{purchaseOrders.filter(o => o.status === 'shipped').length}</p>
                            </div>
                            <div className="bg-white p-4 rounded-xl border border-gray-200">
                                <p className="text-sm text-gray-500 mb-1">This Month Spend</p>
                                <p className="text-2xl font-bold text-emerald-600">${purchaseOrders.reduce((sum, o) => sum + o.total, 0).toLocaleString()}</p>
                            </div>
                        </div>

                        <div className="bg-white rounded-2xl border border-gray-200">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border-b border-gray-100">
                                <div className="flex items-center gap-2">
                                    <Filter size={18} className="text-gray-400" />
                                    {['all', 'draft', 'pending', 'confirmed', 'shipped', 'delivered'].map((f) => (
                                        <button
                                            key={f}
                                            onClick={() => setFilter(f)}
                                            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors capitalize ${
                                                filter === f ? 'bg-rose-100 text-rose-700' : 'text-gray-600 hover:bg-gray-100'
                                            }`}
                                        >
                                            {f}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-gray-100">
                                            <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">PO Number</th>
                                            <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                                            <th className="text-center p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                                            <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                            <th className="text-center p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                            <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Order Date</th>
                                            <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Expected</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredOrders.map((order) => (
                                            <tr key={order.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer">
                                                <td className="p-4">
                                                    <span className="font-mono text-sm font-medium text-rose-600">{order.id}</span>
                                                </td>
                                                <td className="p-4 font-medium text-gray-900">{order.supplier}</td>
                                                <td className="p-4 text-center text-gray-600">{order.items}</td>
                                                <td className="p-4 text-right font-semibold text-gray-900">${order.total.toFixed(2)}</td>
                                                <td className="p-4 text-center">{getStatusBadge(order.status)}</td>
                                                <td className="p-4 text-sm text-gray-600">{order.orderDate}</td>
                                                <td className="p-4 text-sm text-gray-600">{order.deliveryDate || '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </>
                )}

                {/* Suppliers Tab */}
                {activeTab === 'suppliers' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {suppliers.map((supplier) => (
                            <div key={supplier.id} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg transition-shadow">
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <h3 className="font-semibold text-gray-900 text-lg">{supplier.name}</h3>
                                        <p className="text-sm text-gray-500">{supplier.contact}</p>
                                    </div>
                                    <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 rounded-lg">
                                        <span className="text-amber-600 font-bold">{supplier.rating}</span>
                                        <span className="text-amber-500">★</span>
                                    </div>
                                </div>

                                <div className="space-y-2 mb-4">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-500">Email</span>
                                        <span className="text-gray-900">{supplier.email}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-500">Phone</span>
                                        <span className="text-gray-900">{supplier.phone}</span>
                                    </div>
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-gray-500">Lead Time</span>
                                        <span className="font-medium text-gray-900">{supplier.leadTime}</span>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-gray-100 grid grid-cols-2 gap-4">
                                    <div>
                                        <p className="text-xs text-gray-500 mb-1">Orders YTD</p>
                                        <p className="text-xl font-bold text-gray-900">{supplier.ordersYTD}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 mb-1">Spend YTD</p>
                                        <p className="text-xl font-bold text-emerald-600">${supplier.spendYTD.toLocaleString()}</p>
                                    </div>
                                </div>

                                <button className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                                    <FileText size={16} />
                                    View Price List
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Suggested Orders Tab */}
                {activeTab === 'suggested' && (
                    <div className="bg-white rounded-2xl border border-gray-200">
                        <div className="p-4 border-b border-gray-100">
                            <div className="flex items-center gap-2 text-amber-600">
                                <AlertCircle size={20} />
                                <span className="font-medium">These items are below minimum stock levels</span>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b border-gray-100">
                                        <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
                                        <th className="text-center p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Current Stock</th>
                                        <th className="text-center p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Min Level</th>
                                        <th className="text-center p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Suggested Qty</th>
                                        <th className="text-left p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                                        <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Est. Cost</th>
                                        <th className="text-right p-4 text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {suggestedOrders.map((item) => (
                                        <tr key={item.sku} className="border-b border-gray-50 hover:bg-gray-50">
                                            <td className="p-4">
                                                <p className="font-medium text-gray-900">{item.name}</p>
                                                <p className="text-xs text-gray-500 font-mono">{item.sku}</p>
                                            </td>
                                            <td className="p-4 text-center">
                                                <span className={`font-bold ${item.current === 0 ? 'text-red-600' : 'text-amber-600'}`}>
                                                    {item.current}
                                                </span>
                                            </td>
                                            <td className="p-4 text-center text-gray-600">{item.min}</td>
                                            <td className="p-4 text-center">
                                                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-lg font-bold">
                                                    {item.suggested}
                                                </span>
                                            </td>
                                            <td className="p-4 text-gray-900">{item.supplier}</td>
                                            <td className="p-4 text-right font-semibold text-gray-900">
                                                ${(item.suggested * item.unitPrice).toFixed(2)}
                                            </td>
                                            <td className="p-4 text-right">
                                                <button className="flex items-center gap-1 px-3 py-1.5 bg-rose-600 text-white rounded-lg text-sm font-medium hover:bg-rose-700 transition-colors">
                                                    <Send size={14} />
                                                    Order
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="p-4 border-t border-gray-100 flex items-center justify-between">
                            <p className="text-sm text-gray-500">
                                Total estimated cost: <span className="font-bold text-gray-900">
                                    ${suggestedOrders.reduce((sum, i) => sum + (i.suggested * i.unitPrice), 0).toFixed(2)}
                                </span>
                            </p>
                            <button className="flex items-center gap-2 px-4 py-2 bg-rose-600 text-white rounded-lg font-medium hover:bg-rose-700 transition-colors">
                                <ShoppingCart size={18} />
                                Create Order for All
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default ProcurementPage;
